import React, { useState, useEffect } from 'react';
import styles from './TimeSelector.module.css';
import Skeleton from './Skeleton';

const TimeSelector = ({
    planSuggestedDuration,
    remainingMinutes,
    purchasedMinutes = 0,
    purchasedExpiry,
    onTimeSelected,
    isLoading = false,
    recordingTime = 0,
    timeLimit = 0,
    isTimeLimitReached = false,
    isRecording = false
}) => {
    const [inputValue, setInputValue] = useState('');
    const [selectedMinutes, setSelectedMinutes] = useState(0);

    const formatTime = (seconds) => {
        if (typeof seconds !== 'number' || isNaN(seconds)) {
            return '00:00';
        }
        const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
        const secs = Math.floor(seconds % 60).toString().padStart(2, '0');
        return `${mins}:${secs}`;
    };

    const getTimeRemaining = () => {
        if (typeof timeLimit !== 'number' || isNaN(timeLimit)) {
            return '/ 00:00';
        }
        return `/ ${formatTime(timeLimit)}`;
    };

    const getTotalAvailableMinutes = () => {
        const now = new Date();
        const hasValidPurchasedMinutes = purchasedMinutes > 0 &&
            (!purchasedExpiry || new Date(purchasedExpiry) > now);

        return remainingMinutes + (hasValidPurchasedMinutes ? purchasedMinutes : 0);
    };

    useEffect(() => {
        const totalAvailable = getTotalAvailableMinutes();
        const initialValue = Math.min(
            Math.floor(planSuggestedDuration / 60),
            totalAvailable
        );
        setSelectedMinutes(initialValue);
        setInputValue(initialValue.toString());
        // eslint-disable-next-line 
    }, [planSuggestedDuration, remainingMinutes, purchasedMinutes, purchasedExpiry]);

    const handleInputChange = (e) => {
        const value = e.target.value;
        if (!/^\d*$/.test(value)) return;

        setInputValue(value);

        const numValue = value === '' ? 0 : parseInt(value, 10);
        const totalAvailable = getTotalAvailableMinutes();

        // Update the actual value only if it's within bounds
        if (numValue >= 1 && numValue <= totalAvailable) {
            setSelectedMinutes(numValue);
            onTimeSelected(numValue);
        }
    };

    const handleBlur = () => {
        // On blur, force the value to be within bounds
        let validValue = parseInt(inputValue, 10) || 0;
        const totalAvailable = getTotalAvailableMinutes();

        if (validValue < 1) {
            validValue = 1;
        } else if (validValue > totalAvailable) {
            validValue = totalAvailable;
        }

        setSelectedMinutes(validValue);
        setInputValue(validValue.toString());
        onTimeSelected(validValue);
    };

    const handleKeyDown = (e) => {
        const totalAvailable = getTotalAvailableMinutes();
        // Prevent increment/decrement beyond bounds using arrow keys
        if (e.key === 'ArrowUp' && selectedMinutes >= totalAvailable) {
            e.preventDefault();
        }
        if (e.key === 'ArrowDown' && selectedMinutes <= 1) {
            e.preventDefault();
        }

        // Prevent invalid characters
        const invalidKeys = ['e', 'E', '+', '-', '.'];
        if (invalidKeys.includes(e.key)) {
            e.preventDefault();
        }
    };

    const totalAvailable = getTotalAvailableMinutes();
    // eslint-disable-next-line 
    const purchasedMinutesValid = purchasedMinutes > 0 &&
        (!purchasedExpiry || new Date(purchasedExpiry) > new Date());

    return (
        <div className={styles.container}>
            <div className={styles.timerWrapper}>
                <span className={`${styles.timer} ${isTimeLimitReached ? styles.timerLimitReached : ''}`}>
                    {formatTime(recordingTime)} {getTimeRemaining()}
                </span>
            </div>
            {!isRecording && (
                <div className={styles.inputGroup}>
                    <label className={styles.label}>
                        Presentation Duration (minutes):
                        {isLoading ? (
                            <Skeleton
                                width="100px"
                                height="38px"
                                className={styles.skeletonInput}
                            />
                        ) : (
                            <input
                                type="number"
                                value={inputValue}
                                onChange={handleInputChange}
                                onBlur={handleBlur}
                                onKeyDown={handleKeyDown}
                                min={1}
                                max={totalAvailable}
                                className={styles.input}
                                aria-label="Select presentation duration in minutes"
                            />
                        )}
                    </label>
                    <div className={styles.limits}>
                        {isLoading ? (
                            <Skeleton
                                width="200px"
                                height="16px"
                                className={styles.skeletonLimits}
                            />
                        ) : (
                            `Allowed range: 1-${totalAvailable} minutes`
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TimeSelector;